/**
 * Sticky Header Module
 * Handles adding .stuck class to .dipg-header when it becomes sticky after 60px scroll
 */

import { debounce, onDOMReady, devLog } from './utils.js';

class StickyHeader {
    constructor() {
        this.header = null;
        this.stickyOffset = 60; // 60px from top
        this.stickyOffsetRemove = 45; // Hysteresis: remove stuck class at 45px to prevent oscillation (larger gap)
        this.isStuck = false;
        this.stickyTimeout = null;
        this.lastScrollTop = 0;
        this.isTransitioning = false; // Flag to prevent checks during CSS transitions
        this.transitionDuration = 300; // Match CSS transition duration (0.3s = 300ms)
        this.init();
    }

    init() {
        // Wait for DOM to be ready using shared utility
        onDOMReady(() => this.setup());
    }

    setup() {
        devLog('StickyHeader: setup() called');
        
        // Find the .dipg-header element
        this.header = document.querySelector('.dipg-header');
        
        devLog('StickyHeader: Looking for .dipg-header element...');
        devLog('StickyHeader: Found element:', this.header);
        
        if (!this.header) {
            devLog('StickyHeader: .dipg-header element not found');
            devLog('StickyHeader: Available header-like elements:', document.querySelectorAll('[class*="header"]'));
            return;
        }

        devLog('StickyHeader: .dipg-header element found, binding events');
        
        // Bind scroll event
        this.bindEvents();
        
        // Check initial state
        this.checkStickyState();
    }

    bindEvents() {
        // Use debounced scroll listener to prevent rapid firing
        // This helps prevent oscillation when header height changes
        window.addEventListener('scroll', debounce(() => {
            this.checkStickyState();
        }, 10), { passive: true });

        // Re-check on window resize (in case header height changes)
        window.addEventListener('resize', debounce(() => {
            this.checkStickyState();
        }, 250));
    }

    checkStickyState() {
        if (!this.header) return;
        
        // Don't check during transitions to prevent oscillation
        if (this.isTransitioning) {
            return;
        }

        // Clear any existing timeout first
        if (this.stickyTimeout) {
            clearTimeout(this.stickyTimeout);
            this.stickyTimeout = null;
        }

        // Use the most reliable scroll position calculation
        const scrollTop = Math.max(
            window.pageYOffset || 0,
            document.documentElement.scrollTop || 0,
            document.body.scrollTop || 0
        );
        
        // Prevent rapid toggling by checking if scroll position actually changed significantly
        const scrollDelta = Math.abs(scrollTop - this.lastScrollTop);
        if (scrollDelta < 3 && this.isStuck === (scrollTop >= this.stickyOffsetRemove)) {
            // Scroll position hasn't changed enough AND state matches expected, skip check
            this.lastScrollTop = scrollTop;
            return;
        }
        
        this.lastScrollTop = scrollTop;
        
        // Set a timeout to change the sticky state
        // IMPORTANT: Re-check scroll position when timeout fires, don't use captured values
        // This prevents issues when header height changes cause scroll position shifts
        this.stickyTimeout = setTimeout(() => {
            // Don't proceed if we're already transitioning
            if (this.isTransitioning) {
                this.stickyTimeout = null;
                return;
            }
            
            // Re-check scroll position when timeout fires (it may have changed)
            const currentScrollTop = Math.max(
                window.pageYOffset || 0,
                document.documentElement.scrollTop || 0,
                document.body.scrollTop || 0
            );
            
            // Use hysteresis: different thresholds for adding vs removing stuck class
            // Larger gap (60px add, 45px remove = 15px gap) prevents oscillation
            const shouldBeStuck = currentScrollTop >= this.stickyOffset;
            const shouldRemoveStuck = currentScrollTop < this.stickyOffsetRemove;
            
            // Only add stuck class if we're past the threshold and not already stuck
            if (shouldBeStuck && !this.isStuck) {
                this.addStuckClass();
            } 
            // Only remove stuck class if we're below the removal threshold and currently stuck
            // This hysteresis prevents rapid toggling
            else if (shouldRemoveStuck && this.isStuck) {
                this.removeStuckClass();
            }
            
            this.stickyTimeout = null;
        }, 150); // Increased delay to allow layout to settle after header height changes
    }

    addStuckClass() {
        if (this.header && !this.isStuck) {
            // Set transitioning flag to prevent checks during CSS transition
            this.isTransitioning = true;
            
            this.header.classList.add('stuck');
            this.isStuck = true;
            devLog('StickyHeader: Header is now stuck');
            
            // Clear transitioning flag after CSS transition completes
            setTimeout(() => {
                this.isTransitioning = false;
            }, this.transitionDuration + 50); // Add small buffer
        }
    }

    removeStuckClass() {
        if (this.header && this.isStuck) {
            // Set transitioning flag to prevent checks during CSS transition
            this.isTransitioning = true;
            
            this.header.classList.remove('stuck');
            this.isStuck = false;
            devLog('StickyHeader: Header is no longer stuck');
            
            // Clear transitioning flag after CSS transition completes
            setTimeout(() => {
                this.isTransitioning = false;
            }, this.transitionDuration + 50); // Add small buffer
        }
    }
}

export default StickyHeader; 